import {reactive, ref, computed, watch} from "vue";
import {useFetch} from "@/composables/useFetch";
import {useJsonApiFetch} from "@/composables/useJsonApiFetch";
import {notifyCrosslinksChanged} from "@/composables/crosslinkEvents";
import {JudgmentValue, KDM, NodeRelation} from "#/KdmNode";
import {krr} from "@/store/krr";

const ofetch = useFetch();
const jsonApiFetch = useJsonApiFetch();

export const JUDGMENT_COLOR_CHOICES: string[] = [
  '#FFFFFF',
  '#F2F2F2',
  '#E6E6E6',
  '#D9D9D9',
  '#CCCCCC',
  '#BFBFBF',
  '#B3B3B3',
  '#A6A6A6',
  '#999999',
  '#8C8C8C',
  '#7F7F7F',
  '#737373',
  '#666666',
  '#595959',
  '#4D4D4D',
  '#404040',
  '#333333',
  '#262626',
  '#191919',
  '#000000',
];

export const JUDGMENT_COLOR_LABELS: Record<string, string> = {
  '#FFFFFF': 'White',
  '#F2F2F2': 'Snow Gray',
  '#E6E6E6': 'Cloud Gray',
  '#D9D9D9': 'Mist Gray',
  '#CCCCCC': 'Silver Gray',
  '#BFBFBF': 'Aluminum Gray',
  '#B3B3B3': 'Pewter Gray',
  '#A6A6A6': 'Neutral Gray',
  '#999999': 'Ash Gray',
  '#8C8C8C': 'Stone Gray',
  '#7F7F7F': 'Medium Gray',
  '#737373': 'Shadow Gray',
  '#666666': 'Charcoal Gray',
  '#595959': 'Slate Gray',
  '#4D4D4D': 'Graphite',
  '#404040': 'Iron Gray',
  '#333333': 'Obsidian',
  '#262626': 'Night Gray',
  '#191919': 'Deep Charcoal',
  '#000000': 'Black',
};

export const drawerVisible = ref(false);

// Deliberation Matrix Report overlay visibility (opened from the Knot View).
export const reportVisible = ref(false);

// "Add axis value" recycle drawer (pick an existing briquette to crosslink).
export const axisRecycle = ref<{ visible: boolean; axisType: string }>({ visible: false, axisType: 'column' });
export function openAxisRecycle(axisType: string): void {
  axisRecycle.value = { visible: true, axisType };
}
export const indicatorsVisible = ref(false);
export const percentagesVisible = ref(true);
export const evaluationLabelsVisible = ref(true); // NEW: Toggle for evaluation value labels
export const editMode = ref(false);
export const iprExPostModalVisible = ref(false);
export const selectedCell = ref('');
export const cellIndicatorsMapping = reactive<Record<string, Map<string, {title: string, moderation_class: string}>>>({});
export const judgmentOptions = ref<Map<string, JudgmentValue>>(new Map());
export const savingJudgmentOption = ref(false);
export const kdm = ref<KDM | null>(null);
export const globalError = ref<string | null>(null);
export const bsModalState = ref(false);

export const discussionState = reactive({
  visible: false,
  coords: '',
  nodeId: '',
  canWrite: false,
  // Clear (delete) comments is owner / Group coordinator only — stricter than
  // canWrite — so the discussion panel only shows the Clear actions to them.
  canClear: false,
});

// ---- Axis pivot: view the datasheets from Stakeholder (default), TOC or QPI ----
export type ViewAxis = 'stakeholder' | 'toc' | 'qpi';
export const viewAxis = ref<ViewAxis>('stakeholder');
// Active page (tab) for the pivoted (TOC/QPI) views.
export const activePivotId = ref<string | null>(null);

// Values of the currently-selected pivot axis (TOC or QPI).
export const pivotAxisItems = computed(() => {
  if (viewAxis.value === 'toc') return kdm.value?.columns || [];
  if (viewAxis.value === 'qpi') return kdm.value?.rows || [];
  return [];
});

// The resolved active pivot tab object.
export const activePivotTab = computed(() =>
  pivotAxisItems.value.find(a => a.id === activePivotId.value) || pivotAxisItems.value[0] || null
);

// The active Stakeholder tab (stakeholder view), shared so the corner Reset can
// target the presented slice. Synced by KdmMainContent.
export const activeStakeholderId = ref<string | null>(null);

// ---- Reset matrix CONTENT (cell evaluations + comments; never the nodes). ----
// Owner / Group coordinator only (UI gates visibility; server re-checks).
export const resetting = ref(false);
async function postMatrixReset(scope: 'all' | 'slice', axis?: string, pivotId?: string) {
  const node = kdm.value?.nid;
  if (!node || resetting.value) return;
  resetting.value = true;
  try {
    await ofetch(`/epbapi/matrix/${node}/reset`, { method: 'POST', body: { scope, axis, pivotId } });
    await loadKdm(String(node));
  } catch (e: any) {
    alert('Reset failed: ' + (e?.data?.error || e?.message || 'Unknown error'));
  } finally {
    resetting.value = false;
  }
}
export async function resetMatrixAll() {
  if (!window.confirm('Reset the WHOLE matrix?\n\nThis deletes ALL cell content and comments (the axes and indicators are kept). This cannot be undone.')) return;
  await postMatrixReset('all');
}
export async function resetCurrentMatrixSlice() {
  const axis = viewAxis.value;
  const pivotId = axis === 'stakeholder' ? activeStakeholderId.value : (activePivotTab.value?.id ?? null);
  if (!pivotId) {
    alert('Select a specific Stakeholder / Object to Compare / Enjeu first.');
    return;
  }
  if (!window.confirm('Reset the current view?\n\nThis deletes the cell content and comments of the presented slice only. This cannot be undone.')) return;
  await postMatrixReset('slice', axis, String(pivotId));
}

export function axisAvailable(axis: ViewAxis): boolean {
  if (axis === 'stakeholder') return (kdm.value?.stake_holders?.length || 0) > 0;
  if (axis === 'toc') return (kdm.value?.columns?.length || 0) > 0;
  return (kdm.value?.rows?.length || 0) > 0;
}

// Switch the viewing axis, resetting the active page to the first value.
export function setViewAxis(axis: ViewAxis): void {
  if (viewAxis.value === axis) return;
  viewAxis.value = axis;
  if (axis !== 'stakeholder') {
    activePivotId.value = pivotAxisItems.value[0]?.id ?? null;
  }
}

export const isGlobalReloading = ref(false);

const pendingJudgmentSaves = new Map<string, ReturnType<typeof setTimeout>>();

function normalizeJudgmentTitle(raw: string | null | undefined, source: KDM | null): string {
  if (!raw || !raw.trim()) {
    return '';
  }

  const defaultTitle = source?.title ? `${source.title} > Evaluation values` : '';
  if (defaultTitle && raw.trim() === defaultTitle.trim()) {
    return '';
  }

  return raw;
}

function normalizeJudgment(option: JudgmentValue, source: KDM | null): JudgmentValue {
  return {
    ...option,
    id: String(option.id),
    title: normalizeJudgmentTitle(option.title, source),
    color: (option.color?.trim()) || JUDGMENT_COLOR_CHOICES[JUDGMENT_COLOR_CHOICES.length - 1],
    revision_id: option.revision_id ? String(option.revision_id) : undefined,
  };
}

function setJudgmentOptionsFromKdm(current: KDM | null): void {
  if (!current?.judgment_values) {
    judgmentOptions.value = new Map();
    return;
  }

  const sanitized = current.judgment_values.map(option => normalizeJudgment(option, current));
  current.judgment_values = sanitized;

  const next = new Map<string, JudgmentValue>();
  sanitized.forEach(option => {
    if (!option || !option.id) {
      return;
    }
    next.set(String(option.id), option);
  });
  judgmentOptions.value = next;
}

function clearPendingJudgmentSave(pid: string): void {
  const timeout = pendingJudgmentSaves.get(pid);
  if (timeout) {
    clearTimeout(timeout);
    pendingJudgmentSaves.delete(pid);
  }
}

function scheduleJudgmentOptionSave(pid: string): void {
  if (isGlobalReloading.value) {
    return;
  }

  const runPersist = () => {
    clearPendingJudgmentSave(pid);
    void persistJudgmentOption(pid);
  };

  if (typeof window === 'undefined') {
    runPersist();
    return;
  }

  clearPendingJudgmentSave(pid);
  const timeout = window.setTimeout(() => {
    runPersist();
  }, 600);
  pendingJudgmentSaves.set(pid, timeout);
}

export function getCommentIcon(coords: string) {
  const summary = kdm.value?.comment_summary?.[coords];
  if (!summary || summary.count === 0) {
    return { icon: 'bi-chat', class: 'text-muted opacity-50' };
  }
  if (summary.has_new) {
    return { icon: 'bi-chat-dots-fill', class: 'text-danger' };
  }
  return { icon: 'bi-chat-dots', class: 'text-primary' };
}

export function markCommentsAsRead(coords: string) {
  if (kdm.value?.comment_summary?.[coords]) {
    kdm.value.comment_summary[coords].has_new = false;
  }
}

export function updateCommentSummary(coords: string, countDelta: number = 1) {
  if (!kdm.value) return;
  if (!kdm.value.comment_summary) {
    kdm.value.comment_summary = {};
  }
  if (!kdm.value.comment_summary[coords]) {
    kdm.value.comment_summary[coords] = { count: 0, has_new: false };
  }
  kdm.value.comment_summary[coords].count += countDelta;
  // User just added the comment, so it's already read
  kdm.value.comment_summary[coords].has_new = false;
}

// Reset comment counts after a Clear: one coords zone, or ALL of them.
export function clearCommentSummary(coords?: string) {
  const summary = kdm.value?.comment_summary;
  if (!summary) return;
  if (coords) {
    summary[coords] = { count: 0, has_new: false };
  }
  else {
    Object.keys(summary).forEach((k) => { summary[k] = { count: 0, has_new: false }; });
  }
}

export function addIndicatorMetadata(node: any) {
  if (!kdm.value) {
    return;
  }
  if (!kdm.value.iprs) {
    kdm.value.iprs = [];
  }

  if (!kdm.value.iprs.some((ipr: any) => String(ipr.id) === String(node.id))) {
    kdm.value.iprs.push({
      id: String(node.id),
      title: node.label || node.title,
      moderation_class: node.moderation_class || '',
      href: node.href || `/node/${node.id}`,
      is_in_kik: true,
    });
  }
}

function judgmentRelationFromOption(option: JudgmentValue): NodeRelation | null {
  const uuid = option.uuid?.trim();
  const revisionId = option.revision_id ? String(option.revision_id).trim() : '';
  const targetId = option.id ? String(option.id).trim() : '';

  if (!uuid || !revisionId || !targetId) {
    console.error('Missing judgment relation metadata', { option });
    return null;
  }

  return {
    type: 'paragraph--judgment_value',
    id: uuid,
    meta: {
      target_revision_id: revisionId,
      drupal_internal__target_id: targetId,
    },
  };
}

function buildJudgmentRelationsFromOptions(source: JudgmentValue[]): NodeRelation[] {
  const relations: NodeRelation[] = [];
  const seen = new Set<string>();

  source.forEach(option => {
    const relation = judgmentRelationFromOption(option);
    if (!relation || seen.has(relation.id)) {
      return;
    }
    seen.add(relation.id);
    relations.push(relation);
  });

  return relations;
}

export function indicatorTitle(id: string) {
  const found =
    krr.value?.iprs.find(ipr => String(ipr.id) === String(id)) ||
    kdm.value?.iprs?.find((ipr: any) => String(ipr.id) === String(id));
  return found?.title || id;
}

export function getIndicatorInfo(id: string) {
  const found =
    krr.value?.iprs.find(ipr => String(ipr.id) === String(id)) ||
    kdm.value?.iprs?.find((ipr: any) => String(ipr.id) === String(id));
  return {
    title: found?.title || id,
    moderation_class: found?.moderation_class || '',
    href: found?.href || `/node/${id}`,
    is_in_kik: (found as any)?.is_in_kik ?? true,
  };
}

export function updateJudgmentOption(pid: string, updates: Partial<Pick<JudgmentValue, 'title' | 'color'>>): void {
  const current = judgmentOptions.value.get(pid);
  if (!current) {
    return;
  }

  const nextValue: JudgmentValue = {
    ...current,
    ...updates,
  };

  if (nextValue.title === current.title && nextValue.color === current.color) {
    return;
  }

  const next = new Map(judgmentOptions.value);
  next.set(pid, nextValue);
  judgmentOptions.value = next;
  scheduleJudgmentOptionSave(pid);
}

export async function createJudgmentOption(): Promise<void> {
  const paragraph = await ofetch<any>(`/epbapi/paragraph`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
    body: {
      type: 'judgment_value',
      parent_id: kdm.value?.nid,
      parent_type: 'node',
      parent_field_name: 'field_judgment_values',
    },
  });

  if (!paragraph?.uuid) {
    return;
  }

  const defaultColor = JUDGMENT_COLOR_CHOICES[JUDGMENT_COLOR_CHOICES.length - 1];
  const paragraphEntity = await jsonApiFetch(`/jsonapi/paragraph/judgment_value/${paragraph.uuid}`);
  const paragraphAttributes = paragraphEntity?.data?.attributes ?? {};

  const rawInternalId =
    paragraphAttributes?.drupal_internal__id ??
    paragraph.drupal_internal__target_id ??
    paragraph.id;

  const rawRevisionId =
    paragraphAttributes?.drupal_internal__revision_id ??
    paragraph.revision_id;

  if (!rawInternalId || !rawRevisionId) {
    console.error('Unable to resolve newly created judgment metadata', {
      paragraph,
      paragraphEntity,
    });
    throw new Error('Failed to resolve metadata for new judgment value.');
  }

  const newInternalId = String(rawInternalId);
  const newRevisionId = String(rawRevisionId);

  const placeholder: JudgmentValue = normalizeJudgment({
    id: newInternalId,
    uuid: paragraph.uuid,
    title: '',
    description: '',
    color: defaultColor,
    revision_id: newRevisionId,
  }, kdm.value);

  const nextOptions = new Map(judgmentOptions.value);
  nextOptions.set(placeholder.id, placeholder);
  judgmentOptions.value = nextOptions;

  if (kdm.value) {
    const existing = [...(kdm.value.judgment_values ?? [])].filter(item => item.uuid !== placeholder.uuid);
    existing.push(placeholder);
    kdm.value.judgment_values = existing;
  }

  const patchedParagraph = await jsonApiFetch(`/jsonapi/paragraph/judgment_value/${paragraph.uuid}`, {
    method: 'PATCH',
    body: {
      data: {
        type: 'paragraph--judgment_value',
        id: paragraph.uuid,
        attributes: {
          field_judgment_title: '',
          field_judgment_color: defaultColor,
        },
      },
    },
  });

  const latestRevisionId = String(
    patchedParagraph?.data?.attributes?.drupal_internal__revision_id ??
    patchedParagraph?.data?.attributes?.revision_id ??
    newRevisionId ??
    '',
  );

  if (latestRevisionId) {
    const refreshedOption = normalizeJudgment({
      ...placeholder,
      revision_id: latestRevisionId,
    }, kdm.value);

    const refreshedMap = new Map(judgmentOptions.value);
    refreshedMap.set(refreshedOption.id, refreshedOption);
    judgmentOptions.value = refreshedMap;

    if (kdm.value) {
      const updated = (kdm.value.judgment_values ?? []).map(option => {
        if (option.uuid === refreshedOption.uuid) {
          return refreshedOption;
        }
        return option;
      });
      kdm.value.judgment_values = updated;
    }
  }

  const relations = buildJudgmentRelationsFromOptions(kdm.value?.judgment_values ?? []);

  if (relations.length === 0) {
    console.error('No judgment relations assembled for KDM update', {
      entries: kdm.value?.judgment_values,
    });
    throw new Error('Missing judgment relation payload.');
  }

  try {
    await jsonApiFetch(`/jsonapi/node/epbeo_kdm/${kdm.value?.uuid}`, {
      method: 'PATCH',
      body: {
        data: {
          type: 'node--epbeo_kdm',
          id: kdm.value?.uuid,
          relationships: {
            field_judgment_values: {
              data: relations,
            },
          },
        },
      },
    });
  } catch (error) {
    console.error('Failed to update KDM judgment relations', {
      error,
      kdmUuid: kdm.value?.uuid,
      relations,
    });
    throw error;
  }

  const beforeReload = new Map(judgmentOptions.value);
  await loadKdm(kdm.value?.nid as string);

  const merged = new Map(judgmentOptions.value);
  beforeReload.forEach((value, key) => {
    const current = merged.get(key);
    if (!current) {
      merged.set(key, normalizeJudgment(value, kdm.value));
      return;
    }

    const normalizedIncoming = normalizeJudgment(value, kdm.value);

    if (normalizedIncoming.title && !current.title) {
      merged.set(key, { ...current, title: normalizedIncoming.title });
    }

    if (normalizedIncoming.color && normalizedIncoming.color !== current.color) {
      merged.set(key, { ...current, color: normalizedIncoming.color });
    }
  });

  judgmentOptions.value = merged;
}

async function persistJudgmentOption(pid: string): Promise<void> {
  savingJudgmentOption.value = true;
  const obj = judgmentOptions.value.get(pid);
  if (!obj) {
    savingJudgmentOption.value = false;
    return;
  }

  try {
    await jsonApiFetch(`/jsonapi/paragraph/judgment_value/${obj.uuid}`, {
      method: 'PATCH',
      body: {
        data: {
          type: 'paragraph--judgment_value',
          id: obj.uuid,
          attributes: {
            field_judgment_color: obj.color,
            field_judgment_title: obj.title,
          },
        },
      },
    });
  } finally {
    savingJudgmentOption.value = false;
  }
}

export async function saveJudgmentOptions(pid: string): Promise<void> {
  clearPendingJudgmentSave(pid);
  await persistJudgmentOption(pid);
}

export async function deleteJudgmentOption(obj: JudgmentValue): Promise<void> {
  clearPendingJudgmentSave(obj.id);
  await jsonApiFetch(`/jsonapi/paragraph/judgment_value/${obj.uuid}`, { method: 'DELETE' });

  if (kdm.value) {
    kdm.value.judgment_values = (kdm.value.judgment_values ?? []).filter(option => option.uuid !== obj.uuid);
  }

  const nextOptions = new Map(judgmentOptions.value);
  nextOptions.delete(obj.id);
  judgmentOptions.value = nextOptions;

  const relations = buildJudgmentRelationsFromOptions(kdm.value?.judgment_values ?? []);

  try {
    await jsonApiFetch(`/jsonapi/node/epbeo_kdm/${kdm.value?.uuid}`, {
      method: 'PATCH',
      body: {
        data: {
          type: 'node--epbeo_kdm',
          id: kdm.value?.uuid,
          relationships: {
            field_judgment_values: {
              data: relations,
            },
          },
        },
      },
    });
  } catch (error) {
    console.error('Failed to detach KDM judgment relation', {
      error,
      kdmUuid: kdm.value?.uuid,
      relations,
    });
    throw error;
  }

  await loadKdm(kdm.value?.nid as string);
}

export async function loadKdm(nid: string): Promise<void> {
  isGlobalReloading.value = true;
  globalError.value = null;
  return ofetch<KDM>(`/epbapi/kdm/${nid}`)
    .then(response => {
      kdm.value = response;
      setJudgmentOptionsFromKdm(response);
      setTimeout(() => {
        isGlobalReloading.value = false;
      }, 500);
    })
    .catch(error => {
      globalError.value =
        error.data?.errorMessage || error.message || 'An error occurred while loading the KDM.';
      isGlobalReloading.value = false;
    });
}

export async function addAxis(axisType: 'row' | 'column', title: string): Promise<boolean> {
  if (!kdm.value) return false;
  
  try {
    const response = await ofetch<any>(`/epbapi/kdm/${kdm.value.nid}/add-axis`, {
      method: 'POST',
      body: { axis_type: axisType, title }
    });
    
    if (response.success && response.axis) {
      const newAxis = {
        id: response.axis.id,
        uuid: response.axis.uuid,
        title: response.axis.title,
        crossLinkId: response.axis.crossLinkId,
        href: response.axis.href,
        moderation_class: response.axis.moderation_class,
        is_in_kik: true,
      };
      
      // Add to the appropriate array
      if (axisType === 'row') kdm.value.rows.push(newAxis);
      else if (axisType === 'column') kdm.value.columns.push(newAxis);

      // Refresh the crosslinks block (axis values are crosslinked nodes).
      notifyCrosslinksChanged()
      return true;
    }
    return false;
  } catch (error: any) {
    console.error('Failed to add axis:', error);
    globalError.value = error.data?.error || error.message || 'Failed to add axis';
    return false;
  }
}

export interface AxisUsageInfo {
  axisTitle: string;
  otherMatrices: Array<{ id: string; title: string; type: string }>;
  dependentIndicators: Array<{ id: string; title: string }>;
  linkedKrrId: string | null;
  isSharedWithKrr: boolean;
}

export async function checkAxisUsage(axisNodeId: string): Promise<AxisUsageInfo | null> {
  if (!kdm.value) return null;

  try {
    const response = await ofetch<AxisUsageInfo>(`/epbapi/kdm/${kdm.value.nid}/axis-usage/${axisNodeId}`);
    return response;
  } catch (error: any) {
    console.error('Failed to check axis usage:', error);
    globalError.value = error.data?.error || error.message || 'Failed to check axis usage';
    return null;
  }
}

export async function deleteAxis(
  axisType: 'row' | 'column' | 'stakeholder',
  crossLinkId: string,
  deleteNode: boolean = false
): Promise<boolean> {
  if (!kdm.value) return false;

  try {
    await ofetch(`/epbapi/kdm/${kdm.value.nid}/axis/${crossLinkId}`, {
      method: 'DELETE',
      body: { deleteNode }
    });

    // Remove from the appropriate array
    if (axisType === 'row') {
      kdm.value.rows = kdm.value.rows.filter(r => r.crossLinkId !== crossLinkId);
    } else if (axisType === 'column') {
      kdm.value.columns = kdm.value.columns.filter(c => c.crossLinkId !== crossLinkId);
    } else if (axisType === 'stakeholder') {
      kdm.value.stake_holders = kdm.value.stake_holders.filter(s => s.crossLinkId !== crossLinkId);
    }

    // Refresh the crosslinks block (axis values are crosslinked nodes).
    notifyCrosslinksChanged()
    return true;
  } catch (error: any) {
    console.error('Failed to delete axis:', error);
    globalError.value = error.data?.error || error.message || 'Failed to delete axis';
    return false;
  }
}

export const aiEvaluating = ref(false);

/**
 * Ask the backend to auto-fill all empty cells for an "IA Evaluation"
 * stakeholder using the Claude API, then soft-reload the matrix.
 */
export async function generateAiEvaluations(): Promise<{ success: boolean; message: string; stakeholderId: string }> {
  if (!kdm.value) return { success: false, message: 'No matrix loaded', stakeholderId: '' };

  aiEvaluating.value = true;
  try {
    const response = await ofetch<any>(`/epbapi/kdm/${kdm.value.nid}/ai-evaluate`, {
      method: 'POST',
      body: {},
    });

    const stakeholderId = String(response?.stakeholder_id || '');
    // Soft-reload so the new/updated "IA Evaluation" stakeholder shows up in the
    // matrix and carousel (no full page reload needed).
    if (kdm.value?.nid) {
      await loadKdm(String(kdm.value.nid));
    }
    // A newly created stakeholder is a crosslinked node, so refresh the
    // crosslinks block (replaces the former full page reload).
    if (response?.stakeholder_created) {
      notifyCrosslinksChanged();
    }
    return { success: true, message: response?.message || 'AI evaluations generated.', stakeholderId };
  } catch (error: any) {
    const msg = error.data?.error || error.message || 'AI evaluation failed';
    console.error('AI evaluation failed:', error);
    return { success: false, message: msg, stakeholderId: '' };
  } finally {
    aiEvaluating.value = false;
  }
}

export async function addStakeholder(title: string): Promise<boolean> {
  if (!kdm.value) return false;

  try {
    const response = await ofetch<any>(`/epbapi/kdm/${kdm.value.nid}/add-axis`, {
      method: 'POST',
      body: { axis_type: 'stakeholder', title }
    });

    if (response.success && response.axis) {
      const newAxis = {
        id: response.axis.id,
        uuid: response.axis.uuid,
        title: response.axis.title,
        crossLinkId: response.axis.crossLinkId,
        href: response.axis.href,
        moderation_class: response.axis.moderation_class,
        is_in_kik: true,
      };

      kdm.value.stake_holders.push(newAxis);

      // Refresh the crosslinks block (axis values are crosslinked nodes).
      notifyCrosslinksChanged()
      return true;
    }
    return false;
  } catch (error: any) {
    console.error('Failed to add stakeholder:', error);
    globalError.value = error.data?.error || error.message || 'Failed to add stakeholder';
    return false;
  }
}

// Sync: when indicatorsVisible turns off, editMode must turn off too
watch(indicatorsVisible, (newVal) => {
  if (!newVal && editMode.value) {
    editMode.value = false;
  }
});

/**
 * Compute cell resulting weight using Standard algorithm (option 1)
 * Sums weights by Evaluation Value, winner is the one with highest total weight
 * In case of tie, first Evaluation Value in order wins (Veto rule)
 * 
 * @param indicators Array of indicators with evaluation_value_id and weight
 * @param judgmentValuesOrder Array of judgment value IDs in their display order
 * @returns Winning evaluation value ID and its total weight percentage
 */
export function computeStandardAlgorithm(
  indicators: Array<{ evaluation_value_id: string; weight: number }>,
  judgmentValuesOrder: string[]
): { evaluationValueId: string; totalWeight: number } | null {
  if (!indicators || indicators.length === 0) {
    return null;
  }

  // Sum weights by evaluation value
  const weightsByEvaluationValue = new Map<string, number>();
  
  indicators.forEach(indicator => {
    const evalId = String(indicator.evaluation_value_id);
    const weight = Number(indicator.weight) || 0;
    const currentWeight = weightsByEvaluationValue.get(evalId) || 0;
    weightsByEvaluationValue.set(evalId, currentWeight + weight);
  });

  // Total weight across ALL evaluation values (denominator for the percentage).
  let sumAllWeights = 0;
  weightsByEvaluationValue.forEach(weight => {
    sumAllWeights += weight;
  });

  // Find winner: highest weight, or first in order if tie (Veto rule)
  let maxWeight = -1;
  let winnerId: string | null = null;

  // Iterate in judgment values order (Veto rule)
  judgmentValuesOrder.forEach(evalId => {
    const weight = weightsByEvaluationValue.get(evalId) || 0;
    if (weight > maxWeight) {
      maxWeight = weight;
      winnerId = evalId;
    }
  });

  if (winnerId === null) {
    return null;
  }

  // Winner's share of the cell total, as a percentage (0–100):
  // (majority evaluation value weight / sum of all evaluation value weights) * 100.
  // Returning the raw winner weight here is what produced impossible values (e.g. 200%).
  const percentage = sumAllWeights > 0 ? (maxWeight / sumAllWeights) * 100 : 0;

  return {
    evaluationValueId: winnerId,
    totalWeight: percentage,
  };
}

/**
 * Compute cell resulting weight using Count algorithm (option 2)
 * Winner is the judgment value with the most indicators (count), ignoring weights.
 * In case of tie, first Evaluation Value in order wins (Veto rule)
 *
 * @param indicators Array of indicators with evaluation_value_id
 * @param judgmentValuesOrder Array of judgment value IDs in their display order
 * @returns Winning evaluation value ID and its count
 */
export function computeCountAlgorithm(
  indicators: Array<{ evaluation_value_id: string; weight: number }>,
  judgmentValuesOrder: string[]
): { evaluationValueId: string; totalWeight: number } | null {
  if (!indicators || indicators.length === 0) {
    return null;
  }

  // Count indicators by evaluation value (ignore weights)
  const countByEvaluationValue = new Map<string, number>();

  indicators.forEach(indicator => {
    const evalId = String(indicator.evaluation_value_id);
    const currentCount = countByEvaluationValue.get(evalId) || 0;
    countByEvaluationValue.set(evalId, currentCount + 1);
  });

  // Find winner: highest count, or first in order if tie (Veto rule)
  let maxCount = 0;
  let winnerId: string | null = null;

  // Iterate in judgment values order (Veto rule)
  judgmentValuesOrder.forEach(evalId => {
    const count = countByEvaluationValue.get(evalId) || 0;
    if (count > maxCount) {
      maxCount = count;
      winnerId = evalId;
    }
  });

  if (winnerId === null) {
    return null;
  }

  return {
    evaluationValueId: winnerId,
    totalWeight: 100,  // Winner gets 100% for full cell display
  };
}

/**
 * Compute winning judgment for a cell from raw field_matrix_data
 * This is the single source of truth for cell color calculation.
 * Uses the algorithm specified by field_cell_weight_algorithm on the KDM node.
 *
 * @param cellData Raw cell data from field_matrix_data[cellId]
 * @param kdmType KDM type (1, 2, or 3)
 * @returns Object with judgmentId, color, and totalWeight, or null if no winner
 */
export function computeCellWinner(
  cellData: any,
  kdmType: number | undefined
): { judgmentId: string; color: string; totalWeight: number } | null {
  if (!cellData) return null;

  const judgmentOrder = kdm.value?.judgment_values?.map(jv => String(jv.id)) || [];
  const algorithm = kdm.value?.cell_weight_algorithm || 1;

  // KDM Type 1: Simple cell with direct judgment (no algorithm)
  if (kdmType === 1) {
    const judgmentId = cellData.judgmentId;
    if (!judgmentId) return null;

    const judgment = judgmentOptions.value.get(judgmentId);
    return {
      judgmentId,
      color: judgment?.color || '#ffffff',
      totalWeight: cellData.sliderValue || 0
    };
  }

  // KDM Type 2 & 3: Multiple indicators - use configured algorithm
  // Convert cell data format: { [indicatorId]: { judgmentId, sliderValue } }
  const indicators: Array<{ evaluation_value_id: string; weight: number }> = [];

  Object.entries(cellData).forEach(([key, value]: [string, any]) => {
    if (value && typeof value === 'object' && value.judgmentId) {
      indicators.push({
        evaluation_value_id: value.judgmentId,
        weight: value.sliderValue || 0
      });
    }
  });

  if (indicators.length === 0) return null;

  let result: { evaluationValueId: string; totalWeight: number } | null = null;

  // Select algorithm based on field_cell_weight_algorithm
  switch (algorithm) {
    case 1:
      // Standard Algorithm: sum weights by judgment, highest wins, veto on tie
      result = computeStandardAlgorithm(indicators, judgmentOrder);
      break;
    case 2:
      // Count Algorithm: count indicators per judgment, highest count wins, veto on tie
      result = computeCountAlgorithm(indicators, judgmentOrder);
      break;
    default:
      result = computeStandardAlgorithm(indicators, judgmentOrder);
      break;
  }

  if (!result) return null;

  const judgment = judgmentOptions.value.get(result.evaluationValueId);
  return {
    judgmentId: result.evaluationValueId,
    color: judgment?.color || '#ffffff',
    totalWeight: result.totalWeight
  };
}

/**
 * Get cell resulting weight based on KDM type and algorithm
 * 
 * @param cellData Cell data from field_matrix_data
 * @param kdmType KDM type ('1', '2', or '3')
 * @param algorithm Algorithm to use for KDM type 2 & 3 (1 = Standard)
 * @param judgmentValuesOrder Array of judgment value IDs in their display order
 * @returns Object with evaluation value ID and weight, or null
 */
export function getCellResultingWeight(
  cellData: any,
  kdmType: string,
  algorithm: number,
  judgmentValuesOrder: string[]
): { evaluationValueId: string; weight: number } | null {
  if (!cellData) {
    return null;
  }

  // KDM Type 1: Direct slider value (no algorithm)
  if (kdmType === '1') {
    const sliderValue = Number(cellData.slider_value) || 0;
    const evaluationValueId = cellData.evaluation_value_id || '';
    return {
      evaluationValueId,
      weight: sliderValue,
    };
  }

  // KDM Type 2 & 3: Use algorithm to calculate from indicators
  if (kdmType === '2' || kdmType === '3') {
    const indicators = cellData.indicators || [];
    
    if (algorithm === 1) {
      // Standard algorithm
      const result = computeStandardAlgorithm(indicators, judgmentValuesOrder);
      if (!result) {
        return null;
      }
      return {
        evaluationValueId: result.evaluationValueId,
        weight: result.totalWeight,
      };
    }
    
    // Future algorithms can be added here
    // if (algorithm === 2) { ... }
    
    console.warn(`Unknown algorithm ${algorithm} for KDM type ${kdmType}`);
    return null;
  }

  return null;
}
